import { memo, useId, type ElementType } from "react";
import { Link } from "react-router-dom";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { styled } from "../../../../lib/styled";
import { softShadowLifted } from "../../../../theme";
import { CorpusFeedView } from "../../../../lib/corpusFeedView";
import type { CorpusFeed, CorpusFeedStatistics } from "@shared/types/corpus/api.ts";
import { FeedAvatar } from "../FeedAvatar";
import { FeedOutcomeBar } from "../FeedOutcomeBar";
import { Facts } from "../../../primitives/data-display/Facts";

const STATE_CHIP_COLOR = {
  active: "success",
  paused: "warning",
  retired: "default",
} as const;

interface CardProps {
  /** A paused or retired feed is shown quieter than one that runs. */
  $quiet: boolean;
  component?: ElementType;
  to?: string;
}

const Card = styled(Paper)<CardProps>(({ theme, $quiet }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.5),
  overflow: "hidden",
  color: $quiet ? theme.palette.text.secondary : theme.palette.text.primary,
  backgroundColor: $quiet ? theme.palette.action.hover : theme.palette.background.paper,
  textDecoration: "none",
  transition: theme.transitions.create(["box-shadow", "transform"]),
  // The card lifts off the canvas: a deep, soft shadow and a slight grow.
  "&:hover, &:focus-visible": {
    boxShadow: softShadowLifted,
    transform: "scale(1.02)",
  },
  "@media (prefers-reduced-motion: reduce)": {
    "&:hover, &:focus-visible": { transform: "none" },
  },
}));

const Header = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  minWidth: 0,
}));

const Heading = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: theme.spacing(0.5),
  minWidth: 0,
}));

const Name = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightMedium,
  overflow: "hidden",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
}));

const ProgressOverlay = styled("svg")({
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  pointerEvents: "none",
});

interface RunProgressProps {
  done: number;
  total: number;
}

/** A line that fills in around the card's edge, clockwise from the top left, as the run goes on.
 *  Its colour is a gradient that travels along the edge, so a card with a run in progress looks
 *  alive; it holds still for a reader who prefers less motion. */
function RunProgress({ done, total }: RunProgressProps) {
  const theme = useTheme();
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  // An id is a valid fragment only without the colons React puts in its own.
  const gradientId = `run-${useId().replace(/:/g, "")}`;
  const share = total > 0 ? Math.min(done / total, 1) : 0;
  const { dark, light } = theme.palette.primary;
  // The edge is drawn on the card's outline, so only the inner half of the stroke shows.
  const edge = {
    width: "100%",
    height: "100%",
    rx: theme.shape.borderRadius,
    ry: theme.shape.borderRadius,
    fill: "none",
    strokeWidth: theme.spacing(1),
  };

  return (
    <ProgressOverlay
      role="progressbar"
      aria-label="Run progress"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(share * 100)}
    >
      <defs>
        {/* One period of the gradient is half the card's width; moving it by that much, over and
            over, is seamless. */}
        <linearGradient
          id={gradientId}
          gradientUnits="userSpaceOnUse"
          spreadMethod="repeat"
          x1="0%"
          x2="50%"
          y1="0%"
          y2="50%"
        >
          <stop offset="0" stopColor={dark} />
          <stop offset="0.5" stopColor={light} />
          <stop offset="1" stopColor={dark} />
          {reducedMotion ? null : (
            <>
              <animate attributeName="x1" values="0%;50%" dur="2.4s" repeatCount="indefinite" />
              <animate attributeName="x2" values="50%;100%" dur="2.4s" repeatCount="indefinite" />
              <animate attributeName="y1" values="0%;50%" dur="2.4s" repeatCount="indefinite" />
              <animate attributeName="y2" values="50%;100%" dur="2.4s" repeatCount="indefinite" />
            </>
          )}
        </linearGradient>
      </defs>
      <rect
        {...edge}
        stroke={`url(#${gradientId})`}
        pathLength={100}
        strokeDasharray={`${share * 100} 100`}
        style={{ transition: theme.transitions.create("stroke-dasharray") }}
      />
    </ProgressOverlay>
  );
}

interface FeedCardProps {
  feed: CorpusFeed;
  /** Where the card leads. */
  to: string;
  /** What the feed has brought in; undefined while it is still loading. */
  statistics?: CorpusFeedStatistics;
  /** The statistics could not be loaded, so their places show a dash. */
  statisticsUnavailable?: boolean;
}

/**
 * One corpus feed as a card that is a link to the feed: who it is, what it has brought in, when it
 * last and next runs, and, while a run is open, its progress along the card's edge. The statistics
 * arrive after the feed, so the card renders without them and fills in.
 */
export const FeedCard = memo(function FeedCard({
  feed,
  to,
  statistics,
  statisticsUnavailable = false,
}: FeedCardProps) {
  const view = new CorpusFeedView(feed, statistics);
  const waiting = statistics === undefined && !statisticsUnavailable;
  const value = (text: string | null) => (waiting ? <Skeleton width="3em" /> : (text ?? "—"));

  return (
    <Card component={Link} to={to} $quiet={view.isQuiet} aria-label={view.name}>
      {view.openRun ? (
        <RunProgress done={view.openRun.processedCount} total={view.openRun.totalCount} />
      ) : null}

      <Header>
        <FeedAvatar view={view} />
        <Heading>
          <Name variant="subtitle1">{view.name}</Name>
          <Chip label={view.stateLabel} color={STATE_CHIP_COLOR[feed.state]} />
        </Heading>
      </Header>

      <Facts
        items={[
          { label: "Ingested", value: value(view.ingestedText) },
          { label: "Rejection rate", value: value(view.rejectionRateText) },
          { label: "Last run", value: view.lastRunText },
          { label: "Next run", value: view.nextRunText },
        ]}
      />

      {statistics ? <FeedOutcomeBar statistics={statistics} /> : <Skeleton variant="rounded" />}
    </Card>
  );
});
